import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, CheckCircle2, X, Clock, MessageSquare, User, Calendar } from "lucide-react";
import { toast } from "sonner";

const PRIORITY_TONE = {
    low: "bg-zinc-100 text-zinc-600 border-zinc-200",
    normal: "bg-blue-50 text-blue-700 border-blue-200",
    high: "bg-red-50 text-red-700 border-red-200",
};

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" });
    } catch { return iso; }
}
function formatDateTime(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Kolkata" }) + " IST";
    } catch { return iso; }
}
function todayIst() {
    const now = new Date();
    const istMs = now.getTime() + (5.5 * 60 * 60 * 1000) + (now.getTimezoneOffset() * 60 * 1000);
    return new Date(istMs).toISOString().slice(0, 10);
}

export default function ServiceTickets({ stationId }) {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/plants/${stationId}/tickets`);
            setTickets(data);
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail));
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [stationId]);

    const openCount = tickets.filter(t => t.status === "open").length;

    return (
        <div className="bg-white border border-zinc-200 rounded-sm" data-testid="service-tickets-card">
            <div className="p-4 border-b border-zinc-200 flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Service Tickets</div>
                    <div className="text-sm text-zinc-700 mt-0.5">
                        {tickets.length} total · <span className="text-[#DC2626] font-mono">{openCount} open</span> · click any ticket # to add remarks
                    </div>
                </div>
                <Button
                    size="sm"
                    onClick={() => setShowCreate(true)}
                    className="bg-[#0A0A0A] hover:bg-black text-white rounded-full h-8"
                    data-testid="raise-ticket-btn"
                >
                    <Plus className="w-4 h-4 mr-1.5" /> Raise Ticket
                </Button>
            </div>

            {loading ? (
                <div className="p-6 text-sm text-zinc-500">Loading tickets…</div>
            ) : tickets.length === 0 ? (
                <div className="p-6 text-sm text-zinc-500" data-testid="no-tickets">
                    No service tickets yet. Click <span className="text-[#0A0A0A] font-medium">Raise Ticket</span> to create one.
                </div>
            ) : (
                <table className="w-full text-sm" data-testid="tickets-table">
                    <thead>
                        <tr className="text-[10px] uppercase tracking-wider text-zinc-500 bg-zinc-50 border-b border-zinc-200">
                            <th className="py-2 px-4 text-left font-semibold">Ticket #</th>
                            <th className="py-2 px-4 text-left font-semibold">Subject</th>
                            <th className="py-2 px-4 text-left font-semibold">Opened</th>
                            <th className="py-2 px-4 text-left font-semibold">Closed</th>
                            <th className="py-2 px-4 text-left font-semibold">Days Open</th>
                            <th className="py-2 px-4 text-left font-semibold">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tickets.map((t, i) => (
                            <tr key={t.id} className="border-b border-zinc-100 hover:bg-zinc-50/60" data-testid={`ticket-row-${i}`}>
                                <td className="py-2.5 px-4">
                                    <button
                                        onClick={() => setSelectedTicket(t)}
                                        className="font-mono text-xs text-[#0A0A0A] hover:underline hover:text-[#000000] font-medium"
                                        data-testid={`ticket-num-${i}`}
                                    >
                                        {t.ticket_number}
                                    </button>
                                    {(t.remarks || []).length > 0 && (
                                        <span className="ml-2 inline-flex items-center gap-0.5 text-[10px] font-mono text-zinc-500" title={`${t.remarks.length} remark${t.remarks.length === 1 ? "" : "s"}`}>
                                            <MessageSquare className="w-2.5 h-2.5" /> {t.remarks.length}
                                        </span>
                                    )}
                                </td>
                                <td className="py-2.5 px-4">
                                    <button onClick={() => setSelectedTicket(t)} className="text-left" data-testid={`ticket-subject-${i}`}>
                                        <div className="text-sm text-zinc-900 font-medium hover:text-[#0A0A0A]">{t.subject}</div>
                                        {t.description && <div className="text-[11px] text-zinc-500 mt-0.5 line-clamp-1">{t.description}</div>}
                                    </button>
                                </td>
                                <td className="py-2.5 px-4 font-mono text-xs text-zinc-700">{formatDate(t.opening_date)}</td>
                                <td className="py-2.5 px-4 font-mono text-xs text-zinc-700">{t.closing_date ? formatDate(t.closing_date) : "—"}</td>
                                <td className="py-2.5 px-4 font-mono text-xs" data-testid={`ticket-days-${i}`}>
                                    <span className={t.status === "open" ? "text-[#DC2626]" : "text-zinc-500"}>
                                        {t.days_open}d
                                    </span>
                                </td>
                                <td className="py-2.5 px-4">
                                    {t.status === "open" ? (
                                        <span className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-red-50 text-[#DC2626] border border-red-200">
                                            <Clock className="w-2.5 h-2.5" /> OPEN
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-green-50 text-[#16A34A] border border-green-200">
                                            <CheckCircle2 className="w-2.5 h-2.5" /> CLOSED
                                        </span>
                                    )}
                                    <span className={`inline-block ml-1.5 text-[10px] font-mono px-1.5 py-0.5 rounded-sm border ${PRIORITY_TONE[t.priority] || PRIORITY_TONE.normal}`}>
                                        {(t.priority || "normal").toUpperCase()}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {showCreate && (
                <CreateTicketDialog stationId={stationId} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />
            )}
            {selectedTicket && (
                <TicketDetailDialog
                    ticketId={selectedTicket.id}
                    onClose={() => setSelectedTicket(null)}
                    onChanged={() => { load(); }}
                />
            )}
        </div>
    );
}

function CreateTicketDialog({ stationId, onClose, onCreated }) {
    const [subject, setSubject] = useState("");
    const [description, setDescription] = useState("");
    const [priority, setPriority] = useState("normal");
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        if (subject.trim().length < 3) {
            toast.error("Subject must be at least 3 characters");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.post(`/plants/${stationId}/tickets`, {
                subject: subject.trim(),
                description: description.trim() || null,
                priority,
            });
            toast.success(`Ticket ${data.ticket_number} raised`);
            onCreated();
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail));
        } finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="create-ticket-modal">
            <div className="bg-white rounded-sm w-full max-w-lg" onClick={e => e.stopPropagation()}>
                <div className="p-5 border-b border-zinc-200 flex items-center justify-between">
                    <div>
                        <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Raise Service Ticket</div>
                        <div className="text-sm text-zinc-700 mt-0.5">Describe the issue — an engineer will pick it up.</div>
                    </div>
                    <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700" data-testid="close-modal-btn"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-5 space-y-4">
                    <div>
                        <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1.5 block">Subject</label>
                        <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="e.g. Inverter beeping since morning" className="rounded-sm" data-testid="ticket-subject-input" />
                    </div>
                    <div>
                        <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1.5 block">Description (optional)</label>
                        <Textarea value={description} onChange={e => setDescription(e.target.value)} rows={4} placeholder="Any additional details engineers should know…" className="rounded-sm" data-testid="ticket-description-input" />
                    </div>
                    <div>
                        <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1.5 block">Priority</label>
                        <div className="inline-flex rounded-sm border border-zinc-200 overflow-hidden">
                            {["low", "normal", "high"].map(p => (
                                <button
                                    key={p}
                                    onClick={() => setPriority(p)}
                                    className={`px-3 py-1.5 text-xs uppercase tracking-wider font-medium ${priority === p ? "bg-[#0B0D11] text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}
                                    data-testid={`priority-${p}`}
                                >{p}</button>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="p-5 border-t border-zinc-200 flex items-center justify-end gap-2">
                    <Button variant="ghost" onClick={onClose} className="rounded-sm" data-testid="cancel-create-ticket">Cancel</Button>
                    <Button onClick={submit} disabled={saving} className="bg-[#16A34A] hover:bg-[#128F3F] text-white rounded-sm" data-testid="submit-ticket-btn">
                        {saving ? "Raising…" : "Raise Ticket"}
                    </Button>
                </div>
            </div>
        </div>
    );
}

// Full ticket detail modal — remarks timeline + add-remark form + close form (with custom closing date)
function TicketDetailDialog({ ticketId, onClose, onChanged }) {
    const { user } = useAuth();
    const canClose = user && (user.role === "super_admin" || user.role === "sales");
    const [ticket, setTicket] = useState(null);
    const [loading, setLoading] = useState(true);
    const [remark, setRemark] = useState("");
    const [savingRemark, setSavingRemark] = useState(false);
    const [showCloseForm, setShowCloseForm] = useState(false);
    const [closeRemark, setCloseRemark] = useState("");
    const [closingDate, setClosingDate] = useState(todayIst());
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/tickets/${ticketId}`);
            setTicket(data);
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail));
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [ticketId]);

    const submitRemark = async () => {
        if (remark.trim().length < 1) return;
        setSavingRemark(true);
        try {
            const { data } = await api.post(`/tickets/${ticketId}/remarks`, { remark: remark.trim() });
            setTicket(data);
            setRemark("");
            toast.success("Remark added");
            onChanged();
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail));
        } finally { setSavingRemark(false); }
    };

    const submitClose = async () => {
        if (closeRemark.trim().length < 3) {
            toast.error("Resolution remark is required to close (min 3 characters)");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.patch(`/tickets/${ticketId}/close`, {
                resolution_remark: closeRemark.trim(),
                closing_date: closingDate || null,
            });
            setTicket(data);
            setShowCloseForm(false);
            toast.success("Ticket closed");
            onChanged();
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail));
        } finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="ticket-detail-modal">
            <div className="bg-white rounded-sm w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
                <div className="p-5 border-b border-zinc-200 flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                        <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Ticket Detail</div>
                        {ticket && (
                            <div className="flex items-baseline gap-2 mt-0.5 flex-wrap">
                                <div className="font-mono text-sm text-zinc-900 font-medium" data-testid="detail-ticket-number">{ticket.ticket_number}</div>
                                <div className="text-sm text-zinc-700">· {ticket.subject}</div>
                            </div>
                        )}
                    </div>
                    <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700 flex-shrink-0" data-testid="close-detail-btn"><X className="w-4 h-4" /></button>
                </div>

                <div className="p-5 overflow-y-auto flex-1">
                    {loading || !ticket ? (
                        <div className="text-sm text-zinc-500">Loading ticket…</div>
                    ) : (
                        <>
                            {/* Meta */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                                <MetaField label="Opened" value={formatDate(ticket.opening_date)} testid="detail-opened" />
                                <MetaField label="Closed" value={ticket.closing_date ? formatDate(ticket.closing_date) : "—"} testid="detail-closed" />
                                <MetaField label="Days Open" value={`${ticket.days_open}d`} testid="detail-days-open" />
                                <MetaField label="Status" value={ticket.status === "open" ? "OPEN" : "CLOSED"} tone={ticket.status === "open" ? "red" : "green"} testid="detail-status" />
                            </div>

                            {ticket.description && (
                                <div className="mb-5 bg-zinc-50 border border-zinc-200 rounded-sm p-3">
                                    <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1">Initial description</div>
                                    <div className="text-sm text-zinc-700 whitespace-pre-wrap">{ticket.description}</div>
                                </div>
                            )}

                            {/* Timeline */}
                            <div className="mb-5">
                                <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5">
                                    <MessageSquare className="w-3 h-3" /> Remarks Timeline ({(ticket.remarks || []).length})
                                </div>
                                {(ticket.remarks || []).length === 0 ? (
                                    <div className="text-xs text-zinc-500 italic py-2" data-testid="no-remarks">No remarks yet. Be the first to leave a note.</div>
                                ) : (
                                    <div className="space-y-2" data-testid="remarks-timeline">
                                        {ticket.remarks.map((r, i) => (
                                            <div key={r.id || i} className={`rounded-sm p-3 border ${r.is_resolution ? "bg-green-50 border-green-200" : "bg-white border-zinc-200"}`} data-testid={`remark-item-${i}`}>
                                                <div className="flex items-center gap-2 mb-1 text-[11px] font-mono text-zinc-500">
                                                    <User className="w-3 h-3" />
                                                    <span className="text-zinc-800 font-medium">{r.author_name || r.author}</span>
                                                    {r.author_role && <span className="uppercase text-[10px] text-zinc-400">· {r.author_role.replace("_", " ")}</span>}
                                                    <span className="text-zinc-300 ml-auto">{formatDateTime(r.created_at)}</span>
                                                </div>
                                                <div className="text-sm text-zinc-800 whitespace-pre-wrap">{r.text}</div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Add remark */}
                            {ticket.status === "open" && (
                                <div className="border border-zinc-200 rounded-sm p-3 bg-white" data-testid="add-remark-form">
                                    <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1.5 block">Add Remark</label>
                                    <Textarea
                                        value={remark}
                                        onChange={e => setRemark(e.target.value)}
                                        rows={3}
                                        placeholder="Log a site update, part number, engineer note…"
                                        className="rounded-sm"
                                        data-testid="remark-input"
                                    />
                                    <div className="flex items-center justify-end mt-2">
                                        <Button
                                            size="sm"
                                            onClick={submitRemark}
                                            disabled={savingRemark || remark.trim().length < 1}
                                            className="bg-[#0A0A0A] hover:bg-black text-white rounded-full h-8"
                                            data-testid="submit-remark-btn"
                                        >
                                            {savingRemark ? "Saving…" : "Add Remark"}
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {/* Close form */}
                            {canClose && ticket.status === "open" && (
                                <div className="mt-4">
                                    {!showCloseForm ? (
                                        <Button
                                            variant="outline"
                                            onClick={() => setShowCloseForm(true)}
                                            className="rounded-sm w-full"
                                            data-testid="show-close-form-btn"
                                        >
                                            <CheckCircle2 className="w-4 h-4 mr-1.5" /> Close this ticket…
                                        </Button>
                                    ) : (
                                        <div className="border border-green-200 bg-green-50/40 rounded-sm p-3 space-y-3" data-testid="close-form">
                                            <div>
                                                <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1.5 block flex items-center gap-1">
                                                    <Calendar className="w-3 h-3" /> Closing Date
                                                </label>
                                                <Input
                                                    type="date"
                                                    value={closingDate}
                                                    onChange={e => setClosingDate(e.target.value)}
                                                    max={todayIst()}
                                                    className="rounded-sm h-9 max-w-[180px] font-mono"
                                                    data-testid="closing-date-input"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1.5 block">Resolution Remark</label>
                                                <Textarea
                                                    value={closeRemark}
                                                    onChange={e => setCloseRemark(e.target.value)}
                                                    rows={3}
                                                    placeholder="e.g. Panels cleaned by engineer on-site."
                                                    className="rounded-sm bg-white"
                                                    data-testid="close-remark-input"
                                                />
                                            </div>
                                            <div className="flex items-center justify-end gap-2">
                                                <Button variant="ghost" size="sm" onClick={() => setShowCloseForm(false)} className="rounded-sm h-8">Cancel</Button>
                                                <Button
                                                    size="sm"
                                                    onClick={submitClose}
                                                    disabled={saving}
                                                    className="bg-[#16A34A] hover:bg-[#128F3F] text-white rounded-sm h-8"
                                                    data-testid="confirm-close-ticket"
                                                >
                                                    {saving ? "Closing…" : "Close Ticket"}
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {ticket.status === "closed" && ticket.resolution_remark && (
                                <div className="mt-4 bg-green-50 border border-green-200 rounded-sm p-3" data-testid="closed-summary">
                                    <div className="text-[10px] font-semibold uppercase tracking-widest text-green-700 mb-1 flex items-center gap-1.5">
                                        <CheckCircle2 className="w-3 h-3" /> Resolved
                                    </div>
                                    <div className="text-sm text-zinc-800">{ticket.resolution_remark}</div>
                                    <div className="text-[11px] font-mono text-zinc-500 mt-1">
                                        Closed by {ticket.closed_by} · {formatDate(ticket.closing_date)}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function MetaField({ label, value, tone, testid }) {
    const color = tone === "red" ? "text-[#DC2626]" : tone === "green" ? "text-[#16A34A]" : "text-zinc-900";
    return (
        <div data-testid={testid}>
            <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">{label}</div>
            <div className={`text-sm font-mono mt-0.5 ${color}`}>{value}</div>
        </div>
    );
}
